import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  SquaresFour, UsersThree, CalendarBlank, FirstAidKit, Money,
  ListMagnifyingGlass, UserCircle, SignOut, List, X, ClockCountdown, Flask, Eye, IdentificationBadge, ClipboardText, CalendarCheck, BookOpenText, ShieldCheck, GearSix, ClockCounterClockwise, ChartLineUp,
} from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { fileUrl } from "../../lib/api";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, testId: "nav-dashboard" },
  { to: "/timeoff", label: "Time Off", icon: CalendarBlank, testId: "nav-timeoff" },
  { to: "/calendar", label: "Team Calendar", icon: CalendarCheck, testId: "nav-calendar" },
  { to: "/sick-notes", label: "Sick Notes", icon: FirstAidKit, testId: "nav-sick-notes" },
  { to: "/licenses", label: "Licenses", icon: IdentificationBadge, testId: "nav-licenses" },
  { to: "/payroll", label: "Payroll", icon: Money, testId: "nav-payroll" },
  { to: "/history", label: "History", icon: ClockCounterClockwise, perm: "pay.view", testId: "nav-history" },
  { to: "/commissions", label: "Commissions", icon: ChartLineUp, perm: "commission.view", testId: "nav-commissions" },
  { to: "/team", label: "Team", icon: UsersThree, perm: "users.view", testId: "nav-team" },
  { to: "/reviews", label: "Reviews", icon: ClipboardText, perms: ["reviews.complete", "users.manage"], testId: "nav-reviews" },
  { to: "/audit", label: "Audit Trail", icon: ListMagnifyingGlass, perm: "audit.view", testId: "nav-audit" },
  { to: "/access", label: "Access", icon: ShieldCheck, perm: "roles.manage", testId: "nav-access" },
  { to: "/settings", label: "Settings", icon: GearSix, perms: ["settings.manage", "users.manage"], testId: "nav-settings" },
  { to: "/sandbox", label: "Sandbox", icon: Flask, superOnly: true, testId: "nav-sandbox" },
  { to: "/manual", label: "Admin Manual", icon: BookOpenText, perm: "manual.view", testId: "nav-manual" },
  { to: "/profile", label: "My Profile", icon: UserCircle, testId: "nav-profile" },
];

const ROLE_LABEL = { super_admin: "Super Admin", hr_admin: "HR Admin", manager: "Manager", employee: "Employee" };

function NavItems({ onNavigate }) {
  const { user, hasPerm } = useAuth();
  return (
    <nav className="flex-1 space-y-1 px-3">
      {NAV.filter((n) => (!n.superOnly || user?.role === "super_admin") && (!n.perm || hasPerm(n.perm)) && (!n.perms || n.perms.some(hasPerm))).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          onClick={onNavigate}
          data-testid={n.testId}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <n.icon size={19} weight="duotone" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function UserBlock() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const photo = user?.photo_file_path ? fileUrl(user.photo_file_path) : user?.avatar_url;
  return (
    <div className="border-t border-white/10 p-4 flex items-center gap-3">
      <Avatar className="h-9 w-9 border border-white/20">
        <AvatarImage src={photo} alt={user?.full_name} />
        <AvatarFallback className="bg-indigo-600 text-white text-xs">
          {user?.full_name?.split(" ").map((w) => w[0]).join("").slice(0, 2)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.full_name}</p>
        <p className="text-xs text-slate-400 truncate">{ROLE_LABEL[user?.role] || user?.role}</p>
      </div>
      <button
        data-testid="logout-btn"
        onClick={async () => { await logout(); navigate("/login"); }}
        className="text-slate-400 hover:text-white transition-colors"
        title="Sign out"
      >
        <SignOut size={19} />
      </button>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  const exitViewAs = () => {
    const adminToken = localStorage.getItem("cw_admin_token");
    if (adminToken) {
      localStorage.setItem("cw_token", adminToken);
      localStorage.removeItem("cw_admin_token");
    } else {
      localStorage.removeItem("cw_token");
    }
    window.location.href = "/team";
  };

  return (
    <div className="min-h-screen bg-background">
      {user?.view_as && (
        <div data-testid="view-as-banner" className="fixed bottom-0 inset-x-0 z-50 bg-indigo-600 text-white px-4 py-2.5 flex items-center justify-center gap-3 text-sm">
          <Eye size={17} weight="duotone" />
          <span>Viewing as <strong>{user.full_name}</strong> (read-only) — {user.view_as_actor}</span>
          <button data-testid="exit-view-as-btn" onClick={exitViewAs} className="underline font-semibold hover:text-indigo-200 transition-colors">Exit</button>
        </div>
      )}
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col bg-[#0A0F1C] z-40">
        <div className="px-6 py-6 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-indigo-600 flex items-center justify-center">
            <ClockCountdown size={19} weight="bold" className="text-white" />
          </div>
          <span className="font-heading font-semibold text-white text-lg tracking-tight">Team</span>
        </div>
        <NavItems />
        <UserBlock />
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-[#0A0F1C]/95 border-b border-white/10">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-indigo-600 flex items-center justify-center">
              <ClockCountdown size={16} weight="bold" className="text-white" />
            </div>
            <span className="font-heading font-semibold text-white tracking-tight">Team</span>
          </div>
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} className="text-white p-2">
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
        {mobileOpen && (
          <div className="bg-[#0A0F1C] pb-2 border-b border-white/10" data-testid="mobile-nav">
            <NavItems onNavigate={() => setMobileOpen(false)} />
            <UserBlock />
          </div>
        )}
      </header>

      <main className="lg:pl-60">
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-7xl mx-auto">
          {user?.needs_profile_verification && (
            <NavLink to="/profile" data-testid="verification-banner"
              className="mb-6 flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 hover:bg-amber-100 transition-colors">
              <UserCircle size={20} weight="duotone" />
              <span><strong>Profile verification required.</strong> Company policy requires you to review and confirm your personal information every 6 months. Tap here to update.</span>
            </NavLink>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
